import React, { useEffect, useState } from "react";
import { api, imgUrl } from "../../lib/api";
import { toast } from "sonner";
import { Trash2, Pencil, Plus, ExternalLink, Eye, MousePointer, ShoppingBag, Play, Instagram, Youtube, GripVertical, Pin } from "lucide-react";
import AdminDialog from "../../components/admin/AdminDialog";
import SingleImageField from "../../components/common/SingleImageField";

const BLANK = {
  platform: "youtube",
  video_url: "",
  thumbnail_url: "",
  title: "",
  description: "",
  tags: [],
  linked_product_ids: [],
  linked_category_ids: [],
  linked_collection_ids: [],
  creator_id: "",
  display_type: "featured",
  display_order: 0,
  open_mode: "in_app",
  show_follow_button: false,
  social_handle_url: "",
  is_active: true,
};

const DISPLAY_TYPES = [
  { key: "featured", label: "Featured (Style Feed only)" },
  { key: "homepage", label: "Homepage rail + Style Feed" },
  { key: "product_page", label: "Product page only" },
];

export default function AdminSocial() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState(BLANK);
  const [tagsStr, setTagsStr] = useState("");
  const [productIdsStr, setProductIdsStr] = useState("");
  const [categoryIdsStr, setCategoryIdsStr] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [{ data: list }, { data: st }] = await Promise.all([
        api.get("/admin/social"),
        api.get("/admin/social/stats").catch(() => ({ data: null })),
      ]);
      setRows(list || []);
      setStats(st);
      try {
        const { data: cList } = await api.get("/admin/creators");
        setCreators(cList || []);
      } catch {
        /* optional */
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load social content");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditingId(null);
    setForm(BLANK);
    setTagsStr("");
    setProductIdsStr("");
    setCategoryIdsStr("");
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      platform: row.platform,
      video_url: row.video_url || "",
      thumbnail_url: row.thumbnail_url || "",
      title: row.title || "",
      description: row.description || "",
      tags: row.tags || [],
      linked_product_ids: row.linked_product_ids || [],
      linked_category_ids: row.linked_category_ids || [],
      linked_collection_ids: row.linked_collection_ids || [],
      creator_id: row.creator_id || "",
      display_type: row.display_type || "featured",
      display_order: row.display_order || 0,
      open_mode: row.open_mode || "in_app",
      show_follow_button: Boolean(row.show_follow_button),
      social_handle_url: row.social_handle_url || "",
      is_active: row.is_active !== false,
    });
    setTagsStr((row.tags || []).join(", "));
    setProductIdsStr((row.linked_product_ids || []).join(", "));
    setCategoryIdsStr((row.linked_category_ids || []).join(", "));
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.video_url.trim()) { toast.error("Video URL is required"); return; }
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    const payload = {
      ...form,
      creator_id: form.creator_id || null,
      tags: tagsStr.split(",").map((s) => s.trim()).filter(Boolean),
      linked_product_ids: productIdsStr.split(",").map((s) => s.trim()).filter(Boolean),
      linked_category_ids: categoryIdsStr.split(",").map((s) => s.trim()).filter(Boolean),
      display_order: Number(form.display_order) || 0,
    };
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/admin/social/${editingId}`, payload);
        toast.success("Reel updated");
      } else {
        await api.post("/admin/social", payload);
        toast.success("Reel created");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/social/${row.id}`);
      toast.success("Reel deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  // ----- Drag-to-reorder + Pin-to-top -----
  const [dragId, setDragId] = useState(null);
  const [dropId, setDropId] = useState(null);

  const onDragStart = (id) => (e) => {
    setDragId(id);
    try { e.dataTransfer.effectAllowed = "move"; } catch { /* ignore */ }
  };
  const onDragOver = (id) => (e) => {
    e.preventDefault();
    if (id !== dropId) setDropId(id);
  };
  const onDragEnd = () => { setDragId(null); setDropId(null); };

  const commitReorder = async (nextRows) => {
    setRows(nextRows);
    const items = nextRows.map((r, idx) => ({
      id: r.id,
      display_order: nextRows.length - idx, // higher first → preserve visible order
    }));
    try {
      await api.post("/admin/social/reorder", { items });
      toast.success("Order saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reorder failed");
      load();
    }
  };

  const onDrop = (targetId) => async (e) => {
    e.preventDefault();
    if (!dragId || dragId === targetId) { onDragEnd(); return; }
    const srcIdx = rows.findIndex((r) => r.id === dragId);
    const tgtIdx = rows.findIndex((r) => r.id === targetId);
    if (srcIdx < 0 || tgtIdx < 0) { onDragEnd(); return; }
    const next = [...rows];
    const [moved] = next.splice(srcIdx, 1);
    next.splice(tgtIdx, 0, moved);
    onDragEnd();
    await commitReorder(next);
  };

  const pinToTop = async (row) => {
    if (!rows.length || rows[0]?.id === row.id) return;
    const next = [row, ...rows.filter((r) => r.id !== row.id)];
    await commitReorder(next);
  };

  return (
    <div data-testid="admin-social-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="overline mb-1">Content</div>
          <h1 className="font-display text-3xl md:text-4xl">Social Reels</h1>
          <p className="text-sm text-[hsl(var(--kb-muted))] mt-2 max-w-xl">
            Embed Instagram reels and YouTube shorts across the storefront. Tag products for one-tap "Shop this look" flow. Drag rows to reorder, or use the pin icon to send a reel to the top.
          </p>
        </div>
        <button onClick={openNew} className="kb-btn-primary" data-testid="admin-social-new-btn">
          <Plus size={14} className="mr-2" /> Add Reel
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
          <StatCard label="Total reels" value={stats.total} />
          <StatCard label="Active" value={stats.active} />
          <StatCard label="Views" value={stats.views} icon={Eye} />
          <StatCard label="Clicks" value={stats.clicks} icon={MousePointer} />
          <StatCard label="Product clicks" value={stats.product_clicks} icon={ShoppingBag} />
        </div>
      )}

      {error && (
        <div className="mb-6 border border-red-300 bg-red-50 text-red-800 p-3 text-sm">{error}</div>
      )}

      {/* Table */}
      <div className="bg-white border border-[hsl(var(--kb-border))] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[hsl(var(--kb-surface-alt))] text-left">
                <th className="p-3 font-medium">Reel</th>
                <th className="p-3 font-medium">Platform</th>
                <th className="p-3 font-medium">Display</th>
                <th className="p-3 font-medium">Order</th>
                <th className="p-3 font-medium">Products</th>
                <th className="p-3 font-medium">Views / Clicks</th>
                <th className="p-3 font-medium">Status</th>
                <th className="p-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="p-8 text-center text-[hsl(var(--kb-muted))]">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} className="p-8 text-center text-[hsl(var(--kb-muted))]">No reels yet. Click "Add Reel" to create one.</td></tr>
              ) : rows.map((r) => (
                <tr
                  key={r.id}
                  draggable
                  onDragStart={onDragStart(r.id)}
                  onDragOver={onDragOver(r.id)}
                  onDrop={onDrop(r.id)}
                  onDragEnd={onDragEnd}
                  className={`border-t border-[hsl(var(--kb-border))] transition-colors ${
                    dropId === r.id && dragId !== r.id ? "bg-[hsl(var(--kb-surface-alt))]" : ""
                  } ${dragId === r.id ? "opacity-50" : ""}`}
                  data-testid={`admin-social-row-${r.id}`}
                >
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <span
                        className="cursor-grab active:cursor-grabbing text-[hsl(var(--kb-muted))] hover:text-[hsl(var(--kb-text))]"
                        title="Drag to reorder"
                        data-testid={`admin-social-drag-${r.id}`}
                      >
                        <GripVertical size={14} />
                      </span>
                      {r.thumbnail_url ? (
                        <img src={imgUrl(r.thumbnail_url)} alt="" className="w-10 h-14 object-cover bg-neutral-100" />
                      ) : (
                        <div className="w-10 h-14 bg-neutral-100 flex items-center justify-center text-[hsl(var(--kb-muted))]">
                          <Play size={14} />
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="font-medium truncate max-w-[220px]">{r.title}</div>
                        <a
                          href={r.video_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-[hsl(var(--kb-muted))] hover:underline flex items-center gap-1 truncate max-w-[220px]"
                        >
                          <ExternalLink size={10} /> {r.video_id || "open"}
                        </a>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 capitalize">
                    <span className="inline-flex items-center gap-1 text-xs">
                      {r.platform === "instagram" ? <Instagram size={12} /> : <Youtube size={12} />}
                      {r.platform}
                    </span>
                  </td>
                  <td className="p-3 text-xs">{r.display_type}</td>
                  <td className="p-3">{r.display_order}</td>
                  <td className="p-3">{(r.linked_product_ids || []).length}</td>
                  <td className="p-3 text-xs text-[hsl(var(--kb-muted))]">
                    {r.view_count || 0} / {r.click_count || 0}
                    {r.product_click_count ? ` · ${r.product_click_count} shop` : ""}
                  </td>
                  <td className="p-3">
                    <span className={`inline-block text-[11px] px-2 py-0.5 ${r.is_active ? "bg-green-100 text-green-800" : "bg-neutral-200 text-neutral-600"}`}>
                      {r.is_active ? "Active" : "Hidden"}
                    </span>
                  </td>
                  <td className="p-3 text-right">
                    <button
                      onClick={() => pinToTop(r)}
                      className="p-1 hover:text-[hsl(var(--kb-primary))]"
                      title="Pin to top"
                      data-testid={`admin-social-pin-${r.id}`}
                    >
                      <Pin size={14} />
                    </button>
                    <button onClick={() => openEdit(r)} className="p-1 ml-2 hover:text-[hsl(var(--kb-primary))]" title="Edit" data-testid={`admin-social-edit-${r.id}`}>
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => remove(r)} className="p-1 ml-2 hover:text-red-600" title="Delete" data-testid={`admin-social-delete-${r.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <AdminDialog
        open={dialogOpen}
        onClose={() => !saving && setDialogOpen(false)}
        title={editingId ? "Edit reel" : "Add reel"}
        subtitle="Social Content"
        testid="admin-social-dialog"
        maxWidth="max-w-3xl"
        footer={
          <>
            <button onClick={() => setDialogOpen(false)} className="kb-btn-outline" disabled={saving}>Cancel</button>
            <button onClick={save} className="kb-btn-primary" disabled={saving} data-testid="admin-social-save-btn">
              {saving ? "Saving…" : editingId ? "Update" : "Create"}
            </button>
          </>
        }
      >
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Platform">
            <select
              value={form.platform}
              onChange={(e) => setForm({ ...form, platform: e.target.value })}
              className="kb-input"
              data-testid="admin-social-platform"
            >
              <option value="youtube">YouTube Shorts</option>
              <option value="instagram">Instagram Reel/Post</option>
            </select>
          </Field>
          <Field label="Display placement">
            <select
              value={form.display_type}
              onChange={(e) => setForm({ ...form, display_type: e.target.value })}
              className="kb-input"
              data-testid="admin-social-display-type"
            >
              {DISPLAY_TYPES.map((d) => (
                <option key={d.key} value={d.key}>{d.label}</option>
              ))}
            </select>
          </Field>
          <Field label="Creator (optional)" className="md:col-span-2">
            <select
              value={form.creator_id || ""}
              onChange={(e) => setForm({ ...form, creator_id: e.target.value })}
              className="kb-input"
              data-testid="admin-social-creator"
            >
              <option value="">— No creator assigned —</option>
              {creators.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.display_name} (@{c.handle}){c.linked_seller?.shop_name ? ` · ${c.linked_seller.shop_name}` : ""}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Video URL" className="md:col-span-2">
            <input
              type="url"
              value={form.video_url}
              onChange={(e) => setForm({ ...form, video_url: e.target.value })}
              className="kb-input"
              placeholder={form.platform === "instagram" ? "https://www.instagram.com/reel/..." : "https://www.youtube.com/shorts/..."}
              data-testid="admin-social-video-url"
            />
          </Field>
          <Field label="Title" className="md:col-span-2">
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="kb-input"
              maxLength={160}
              data-testid="admin-social-title"
            />
          </Field>
          <Field label="Description" className="md:col-span-2">
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="kb-input"
              rows={2}
              maxLength={800}
              data-testid="admin-social-description"
            />
          </Field>
          <Field label="Thumbnail (optional — auto-filled for YouTube)" className="md:col-span-2">
            <SingleImageField
              value={form.thumbnail_url}
              onChange={(key) => setForm({ ...form, thumbnail_url: key || "" })}
              hint="Required for Instagram. Auto-derived from YouTube URL if blank."
              aspect="aspect-[9/16]"
              testid="admin-social-thumbnail"
            />
          </Field>
          <Field label="Tags (comma separated)" className="md:col-span-2">
            <input
              type="text"
              value={tagsStr}
              onChange={(e) => setTagsStr(e.target.value)}
              className="kb-input"
              placeholder="saree, bridal, festive"
            />
          </Field>
          <Field label="Linked product IDs (comma separated)" className="md:col-span-2">
            <input
              type="text"
              value={productIdsStr}
              onChange={(e) => setProductIdsStr(e.target.value)}
              className="kb-input"
              placeholder="uuid1, uuid2 — shown as 'Shop this look'"
              data-testid="admin-social-product-ids"
            />
          </Field>
          <Field label="Linked category IDs (comma separated)">
            <input
              type="text"
              value={categoryIdsStr}
              onChange={(e) => setCategoryIdsStr(e.target.value)}
              className="kb-input"
            />
          </Field>
          <Field label="Display order (higher first)">
            <input
              type="number"
              value={form.display_order}
              onChange={(e) => setForm({ ...form, display_order: e.target.value })}
              className="kb-input"
            />
          </Field>
          <Field label="Open mode">
            <select
              value={form.open_mode}
              onChange={(e) => setForm({ ...form, open_mode: e.target.value })}
              className="kb-input"
            >
              <option value="in_app">In-app player</option>
              <option value="redirect">Redirect to platform</option>
            </select>
          </Field>
          <Field label="Social handle URL (optional)">
            <input
              type="url"
              value={form.social_handle_url}
              onChange={(e) => setForm({ ...form, social_handle_url: e.target.value })}
              className="kb-input"
              placeholder="https://instagram.com/yourbrand"
            />
          </Field>
          <label className="flex items-center gap-2 text-sm md:col-span-2">
            <input
              type="checkbox"
              checked={form.show_follow_button}
              onChange={(e) => setForm({ ...form, show_follow_button: e.target.checked })}
            /> Show "Follow us" button in player
          </label>
          <label className="flex items-center gap-2 text-sm md:col-span-2">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              data-testid="admin-social-active"
            /> Active (visible on storefront)
          </label>
        </div>
      </AdminDialog>
    </div>
  );
}

function Field({ label, className = "", children }) {
  return (
    <label className={`block text-sm ${className}`}>
      <div className="mb-1 text-[hsl(var(--kb-muted))] text-xs uppercase tracking-wider">{label}</div>
      {children}
    </label>
  );
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="bg-white border border-[hsl(var(--kb-border))] p-4">
      <div className="flex items-center gap-2 text-[hsl(var(--kb-muted))] text-xs uppercase tracking-wider">
        {Icon && <Icon size={12} />} {label}
      </div>
      <div className="font-display text-2xl mt-1">{(value ?? 0).toLocaleString()}</div>
    </div>
  );
}
